import type {
    EditPricelistResponse,
    PackageOfferingDto,
    StandardOfferingDto,
} from "app/shared/model/EditPricelist";

import { isFeeOffering, isPackageOffering } from "./guards";
import { offeringKey as offeringKeyBase } from "./keys";
import { normIso } from "../currency/normalizeCurrency";
import { normalizeDate } from "../date/iso";
import { sanitizeDateStrategy } from "../dateStrategy/normalize";
import { sanitizePeriod, toInt } from "./sanitizePeriod";
import {normalizeFeeOfferingType} from "app/pages/PricelistNew/domain/offering/fee";

export const normalizeForCompare = (
    o: EditPricelistResponse,
    fallbackCurrencyIso: string
) => {
    if (isFeeOffering(o)) {
        const oneOff = [...((o as any)?.oneOffFeePrices ?? [])]
            .map((p: any) => ({
                year: toInt(p?.year),
                currencyIsoCode: normIso(p?.currencyIsoCode),
                amount: Number(p?.amount ?? 0),
            }))
            .sort((a, b) => a.year - b.year || a.currencyIsoCode.localeCompare(b.currencyIsoCode));

        const periods = [...((o as any)?.supplementPeriods ?? [])]
            .map((p: any) => ({
                year: toInt(p?.year),
                startDate: normalizeDate(p?.startDate),
                endDate: normalizeDate(p?.endDate),
                prices: [...(p?.prices ?? [])]
                    .map((x: any) => ({
                        currencyIsoCode: normIso(x?.currencyIsoCode),
                        pricePerMajorUnit: Number(x?.pricePerMajorUnit ?? 0),
                        pricePerMinorUnit: Number(x?.pricePerMinorUnit ?? 0),
                    }))
                    .sort((a, b) => a.currencyIsoCode.localeCompare(b.currencyIsoCode)),
            }))
            .sort((a, b) =>
                a.year - b.year ||
                String(a.startDate).localeCompare(String(b.startDate)) ||
                String(a.endDate).localeCompare(String(b.endDate))
            );

        const applicableProducts = [...((o as any)?.applicableProducts ?? [])]
            .map((ap: any) => ({
                productTypeId: toInt(ap?.productTypeId),
                selectionMode: String(ap?.selectionMode ?? ""),
                productIds: [...(ap?.productIds ?? [])]
                    .map(toInt)
                    .filter((x: number) => Number.isFinite(x))
                    .sort((a: number, b: number) => a - b),
                accommodationTypeIds: [...(ap?.accommodationTypeIds ?? [])]
                    .map(toInt)
                    .filter((x: number) => Number.isFinite(x))
                    .sort((a: number, b: number) => a - b),
                addOnTypeIds: [...(ap?.addOnTypeIds ?? [])]
                    .map(toInt)
                    .filter((x: number) => Number.isFinite(x))
                    .sort((a: number, b: number) => a - b),
                minDuration: ap?.minDuration == null ? null : Number(ap.minDuration),
                maxDuration: ap?.maxDuration == null ? null : Number(ap.maxDuration),
            }))
            .sort((a, b) => a.productTypeId - b.productTypeId);

        const supplementMaximumUnits = {
            hasMaximum: !!(o as any)?.supplementMaximumUnits?.hasMaximum,
            majorUnits: Number((o as any)?.supplementMaximumUnits?.majorUnits ?? 0),
            minorUnits: Number((o as any)?.supplementMaximumUnits?.minorUnits ?? 0),
        };

        return {
            kind: "fee",
            type: o.type,
            key: offeringKeyBase(o as any, fallbackCurrencyIso),
            feeId: (o as any)?.feeId?.id ?? null,
            feeUnitTypeId: toInt((o as any)?.feeUnitTypeId),
            feeOfferingType: String((o as any)?.feeOfferingType ?? ""),
            minimumAge: toNullablePositiveInt((o as any)?.minimumAge),
            maximumAge: toNullablePositiveInt((o as any)?.maximumAge),
            applicableProducts,
            supplementHasSpecificDates: !!(o as any)?.supplementHasSpecificDates,
            supplementMaximumUnits,
            supplementPeriods: periods,
            oneOffFeePrices: oneOff,
        };
    }

    const stdOrPkg = o as StandardOfferingDto | PackageOfferingDto;

    const periods = (stdOrPkg.pricePeriods ?? [])
        .map(sanitizePeriod)
        .map((p) => ({
            ...p,
            priceBands: [...(p.priceBands ?? [])]
                .map((b) => ({
                    ...b,
                    prices: [...(b.prices ?? [])].sort((a, b) =>
                        String(a.currencyIsoCode).localeCompare(String(b.currencyIsoCode))
                    ),
                }))
                .sort(
                    (a, b) =>
                        a.minUnits - b.minUnits || a.maxUnits - b.maxUnits
                ),
        }))
        .sort(
            (a, b) =>
                String(a.startDate).localeCompare(String(b.startDate)) ||
                String(a.endDate).localeCompare(String(b.endDate))
        );

    const strategies = (stdOrPkg.dateStrategyAssignments ?? [])
        .map(sanitizeDateStrategy)
        .map((ds) => ({
            strategyTypeId: ds.strategyTypeId,
            fromDate: ds.fromDate,
            toDate: ds.toDate ?? null,
            isArchived: !!ds.isArchived,
            recurranceStartDays: [...(ds.recurranceStartDays ?? [])]
                .map(toInt)
                .sort((a, b) => a - b),
            specificStartDates: [...(ds.specificStartDates ?? [])]
                .map((x) => normalizeDate((x as any)?.date ?? x))
                .filter(Boolean)
                .sort(),
            fixedDurationDates: [...(ds.fixedDurationDates ?? [])].map((x) => ({
                startDate: normalizeDate((x as any)?.startDate),
                endDate: normalizeDate((x as any)?.endDate),
            })),
        }))
        .sort(
            (a, b) =>
                String(a.fromDate).localeCompare(String(b.fromDate)) ||
                a.strategyTypeId - b.strategyTypeId
        );

    return {
        kind: isPackageOffering(o) ? "package" : "standard",
        type: o.type,
        key: offeringKeyBase(o as any, fallbackCurrencyIso),
        productId: (stdOrPkg as any)?.productId?.id ?? null,
        schoolIds: [...(stdOrPkg as any)?.schoolIds ?? []]
            .map((s: any) => ({ id: toInt((s as any)?.id ?? s) }))
            .filter((s: any) => Number.isFinite(s.id))
            .sort((a: any, b: any) => a.id - b.id),
        pricePeriods: periods,
        dateStrategyAssignments: strategies,
        decommissionDate: normalizeDate((stdOrPkg as any)?.decommissionDate) ?? null,
        packageOfferingBreakdowns: isPackageOffering(o)
            ? (o as any)?.packageOfferingBreakdowns ?? []
            : undefined,
    };
};

export const isSameOfferingForUpdate = (
    a: EditPricelistResponse,
    b: EditPricelistResponse,
    fallbackCurrencyIso: string
) =>
    JSON.stringify(normalizeForCompare(a, fallbackCurrencyIso)) ===
    JSON.stringify(normalizeForCompare(b, fallbackCurrencyIso));

const normalizeSupplementPeriodsForApi = (
    rawPeriods: any[],
    hasSpecificDates: boolean,
    selectedYear: number,
    enabledCurrencies: string[]
) => {
    const mapped = (rawPeriods ?? [])
        .map((p: any) => ({
            year: toInt(p?.year),
            startDate: p?.startDate ?? null,
            endDate: p?.endDate ?? null,
            prices: (p?.prices ?? [])
                .map((x: any) => ({
                    currencyIsoCode: normIso(x?.currencyIsoCode),
                    pricePerMajorUnit: Number(x?.pricePerMajorUnit ?? 0),
                    pricePerMinorUnit: Number(x?.pricePerMinorUnit ?? 0),
                }))
                .filter((x: any) => !!x.currencyIsoCode),
        }))
        .filter((p: any) => Number.isFinite(p.year));

    if (hasSpecificDates) {
        return mapped;
    }

    if (mapped.length === 0) {
        return [
            {
                year: selectedYear,
                startDate: null,
                endDate: null,
                prices: enabledCurrencies.map((currencyIsoCode) => ({
                    currencyIsoCode: normIso(currencyIsoCode),
                    pricePerMajorUnit: 0,
                    pricePerMinorUnit: 0,
                })),
            },
        ];
    }

    const first = mapped[0];
    return [
        {
            year: Number(first.year ?? selectedYear),
            startDate: null,
            endDate: null,
            prices: [...(first.prices ?? [])],
        },
    ];
};

export const prepareOfferingForApi = (
    item: EditPricelistResponse,
    selectedYear: number,
    enabledCurrencies: string[]
): EditPricelistResponse => {

    const schoolIdsFromStatuses = (src: any): { id: number }[] => {
        const schoolIds = Array.isArray(src?.schoolIds) ? src.schoolIds : [];
        return schoolIds
            .map((s: any) => ({ id: toInt((s as any)?.id ?? s) }))
            .filter((s: any) => Number.isFinite(s.id));
    };

    const schoolStatusesOrEmpty = (src: any) =>
        (Array.isArray(src?.schoolStatuses) ? src.schoolStatuses : []) as any[];

    const hasPendingChanges =
        !!(item as any)?.hasPendingChanges || !!(item as any)?.isUpdated || !!(item as any)?.isNew;

    if (isFeeOffering(item)) {
        const productIdText =
            item.productIdText ||
            (item.feeId?.id != null ? `Fee-${item.feeId.id}` : "");

        const feeOfferingType = normalizeFeeOfferingType((item as any).feeOfferingType);
        const isSupplement = feeOfferingType === "Supplement";
        const isOneOff = feeOfferingType === "One Off";

        const rawSupplementPeriods = ((item as any).supplementPeriods ?? []) as any[];

        const mappedSupplementPeriods = isSupplement
            ? normalizeSupplementPeriodsForApi(
                rawSupplementPeriods,
                !!(item as any).supplementHasSpecificDates,
                selectedYear,
                enabledCurrencies
            )
            : [];

        const mappedOneOffFeePrices = ((item as any).oneOffFeePrices ?? [])
            .map((x: any) => ({
                year: toInt(x?.year),
                currencyIsoCode: normIso(x?.currencyIsoCode),
                amount: Number(x?.amount ?? 0),
            }))
            .filter((x: any) => Number.isFinite(x.year) && !!x.currencyIsoCode);

        return {
            offeringType: "fee",
            type: "Fee",
            productIdText,
            schoolIds: schoolIdsFromStatuses(item),
            schoolStatuses: schoolStatusesOrEmpty(item),
            name: String(item.name ?? ""),
            hasPendingChanges,
            isUpdated: !!item.isUpdated,
            isNew: !!item.isNew,

            feeId: item.feeId,
            feeUnitTypeId: toInt((item as any).feeUnitTypeId),
            feeOfferingType,

            activeFromPricingYear: isSupplement
                ? ((item as any).activeFromPricingYear ?? null)
                : null,
            activeToPricingYear: isSupplement
                ? ((item as any).activeToPricingYear ?? null)
                : null,
            minimumAge: (item as any).minimumAge ?? null,
            maximumAge: (item as any).maximumAge ?? null,

            supplementHasSpecificDates: isSupplement
                ? !!(item as any).supplementHasSpecificDates
                : false,

            supplementPeriods: isSupplement ? mappedSupplementPeriods : [],

            supplementMaximumUnits: isSupplement
                ? ((item as any).supplementMaximumUnits ?? {
                    hasMaximum: false,
                    majorUnits: 0,
                    minorUnits: 0,
                })
                : {
                    hasMaximum: false,
                    majorUnits: 0,
                    minorUnits: 0,
                },

            oneOffFeePrices: isOneOff ? mappedOneOffFeePrices : [],

            applicableProducts: (item as any).applicableProducts ?? [],
        } as any;
    }

    if (isPackageOffering(item)) {
        const productIdText = item.productIdText || (item.productId ? `${item.type}-${item.productId.id}` : "");

        return {
            offeringType: "package",
            type: item.type,
            productIdText,
            schoolIds: schoolIdsFromStatuses(item),
            schoolStatuses: schoolStatusesOrEmpty(item),
            name: String(item.name ?? ""),
            hasPendingChanges,
            isUpdated: !!item.isUpdated,
            isNew: !!item.isNew,

            productId: item.productId,
            pricePeriods: (item.pricePeriods ?? []).map(sanitizePeriod),
            dateStrategyAssignments: (item.dateStrategyAssignments ?? []).map(
                sanitizeDateStrategy
            ),

            packageOfferingBreakdowns: (item as any).packageOfferingBreakdowns ?? [],
        } as any;
    }

    const std = item as StandardOfferingDto;
    const productIdText = std.productIdText || (std.productId ? `${std.type}-${std.productId.id}` : "");

    return {
        offeringType: "standard",
        type: std.type,
        productIdText,
        schoolIds: schoolIdsFromStatuses(std),
        schoolStatuses: schoolStatusesOrEmpty(std),
        name: String(std.name ?? ""),
        hasPendingChanges,
        isUpdated: !!std.isUpdated,
        isNew: !!std.isNew,

        productId: std.productId,
        pricePeriods: (std.pricePeriods ?? []).map(sanitizePeriod),
        dateStrategyAssignments: (std.dateStrategyAssignments ?? []).map(
            sanitizeDateStrategy
        ),
    } as any;
};

const toNullablePositiveInt = (v: any): number | null => {
    if (v == null) return null;
    const n = toInt(v);
    return n > 0 ? n : null;
};

